/** Offline photo queue for when the network is unavailable. */

const DB_NAME = "receipts-offline";
const STORE = "pending";
const DB_VERSION = 2;

export interface PendingUpload {
  id: string;
  blob: Blob;
  originalBlob?: Blob;
  filename: string;
  contentType: string;
  createdAt: number;
  transactionId?: number;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "id" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function queuePhoto(
  blob: Blob,
  filename: string,
  contentType: string,
  transactionId?: number,
  originalBlob?: Blob,
): Promise<string> {
  const db = await openDb();
  const id = crypto.randomUUID();
  const item: PendingUpload = {
    id,
    blob,
    filename,
    contentType,
    createdAt: Date.now(),
    transactionId,
    ...(originalBlob ? { originalBlob } : {}),
  };
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).put(item);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  return id;
}

export async function listPending(): Promise<PendingUpload[]> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readonly");
    const req = tx.objectStore(STORE).getAll();
    req.onsuccess = () => resolve(req.result as PendingUpload[]);
    req.onerror = () => reject(req.error);
  });
}

export async function removePending(id: string): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function clearPendingQueue(): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).clear();
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}
